<template>
  <div class="cancel-order-root">
    <GlobalNavigation />

    <main class="py-8 mb-16">

      <BaseContainer>
        <div class="text-center">
          <BaseButton color="muted" to="/">
            <DropdownIcon class="rotate-90 h-4 w-4 mr-2 fill-text" />
            {{ $t('common.back_to_overview') }}
          </BaseButton>
    
          <h1 class="mb-6 mt-6 h1">{{ $t('cancel_order.title') }}</h1>
        </div>
      </BaseContainer>

      <Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
        <BaseContainer>
          <div class="max-w-[450px] mx-auto">
  
            <p>{{ $t('cancel_order.description') }}</p>

            <div class="mb-4 mt-4">
              <InputText
                id="email"
                name="email"
                type="email"
                :rules="rules.email"
                v-model="form.email"
                :placeholder="$t('cancel_order.email_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <div class="mb-4">
              <InputText
                id="order_id"
                name="order_id"
                type="text"
                :rules="rules.order_id"
                v-model="form.order_id"
                :placeholder="$t('cancel_order.order_id_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <BaseButton
              class="w-full relative group"
              color="primary"
              size="lg"
              type="submit"
              :disabled="isSubmitting"
            >
              <span class="w-full flex items-center justify-center">
                {{ $t('cancel_order.submit') }}
                <ArrowRightIcon
                  class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
                />
              </span>
            </BaseButton>

            <div v-if="errorMessage" class="text-sm block pt-2 text-error">
              {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="text-sm block pt-2 text-success">
              {{ successMessage }}
            </div>
          </div>
        </BaseContainer>
      </Form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { Form, type InvalidSubmissionHandler, type SubmissionHandler } from 'vee-validate';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { cancelOrder } from '@/api/cancelOrder';
import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
import DropdownIcon from '@/components/icons/DropdownIcon.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseContainer from '@/components/ui/BaseContainer.vue';
import InputText from '@/components/ui/inputs/InputText.vue';

const { t } = useI18n();

useHead({
  title: t('cancel_order.head_title'),
  meta: [
    { name: 'description', content: t('cancel_order.description') },
    { property: 'og:title', content: t('cancel_order.head_title') },
    { property: 'og:description', content: t('cancel_order.description') },
  ],
});

const form = reactive({ email: '', order_id: '' });

const rules = {
  email: yup
    .string()
    .email(t('validation.email'))
    .required(t('validation.required')),
  order_id: yup
    .string()
    .matches(/^[0-9]+$/, t('cancel_order.error_invalid_order_id'))
    .required(t('validation.required')),
};

const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);
const isSubmitting = ref(false);

const handleSubmit: SubmissionHandler = async (values) => {
  const { email, order_id } = values as { email: string; order_id: string };

  isSubmitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const result = await cancelOrder(email, order_id);

    if (result.status === 'ok') {
      successMessage.value = t('cancel_order.success_message');
    } else if (result.error === 'cancel_tickets_order_not_found') {
      errorMessage.value = t('cancel_order.error_order_not_found');
    } else if (result.error === 'cancel_tickets_invalid_email') {
      errorMessage.value = t('cancel_order.error_invalid_email');
    } else if (result.error === 'cancel_tickets_invalid_order_id') {
      errorMessage.value = t('cancel_order.error_invalid_order_id');
    } else if (result.error === 'cancel_tickets_price_is_above_zero') {
      errorMessage.value = t('cancel_order.error_price_above_zero');
    } else if (result.error === 'cancel_tickets_already_cancelled') {
      errorMessage.value = t('cancel_order.error_already_cancelled');
    } else if (result.error === 'cancel_tickets_already_refunded') {
      errorMessage.value = t('cancel_order.error_already_refunded');
    } else {
      errorMessage.value = t('cancel_order.error_generic');
    }
  } catch {
    errorMessage.value = t('cancel_order.error_generic');
  } finally {
    isSubmitting.value = false;
  }
};

const onInvalidSubmit: InvalidSubmissionHandler = () => {
  // vee-validate handles field-level errors; nothing extra needed here
};
</script>

<style>
.min-h-screen:has( .cancel-order-root) {
  min-height: calc(100vh - 538px) !important;
}
</style>
